import { Fragment, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    HelpOutline as HelpIcon,
} from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import IconButton from '@/components/ui/IconButton/IconButton';
import Typography from '@/components/ui/Typography/Typography';
import ConfirmDialog from '@/components/business/ConfirmDialog/ConfirmDialog';
import { pricebookApi, type ConflictPayload, type PricebookRule } from '@/api/pricebook';
import type { CatalogEntry } from '@/api/catalog';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import AddRuleDialog from '../AddRuleDialog/AddRuleDialog';
import ConflictDialog from '../ConflictDialog/ConflictDialog';
import DocumentationModal from '../DocumentationModal/DocumentationModal';
import type { RulesTableProps } from './IRulesTable';
import { styles } from './styles';

interface ServiceInfo {
    service_type: string;
    repair_type: string;
    part: string;
    position: string;
}

const lookup = (catalog: CatalogEntry[], id: number): ServiceInfo => {
    const entry = catalog.find((c) => c.id === id);
    return {
        service_type: entry?.service_type ?? '-',
        repair_type: entry?.repair_type ?? '-',
        part: entry?.part ?? '-',
        position: entry?.position ?? '-',
    };
};

const formatYearRange = (range: PricebookRule['year_range']): string => {
    if (!range) return 'All Years';
    const { start, end } = range;
    if (!start && !end) return 'All Years';
    const dispStart = start && start < 9999 ? start : null;
    const dispEnd = end && end < 9999 ? end : null;
    if (!dispStart && !dispEnd) return 'All Years';
    if (dispStart === dispEnd) return String(dispStart);
    return `${dispStart ?? '∞'} - ${dispEnd ?? '∞'}`;
};

const formatList = (
    arr: string[] | null | undefined,
    fallback: string,
): string => (arr && arr.length > 0 ? arr.join(', ') : fallback);

const RulesTable = ({
    pricebook,
    rules,
    loading,
    catalog,
    vehicleMakes,
    companyName,
    onRulesChanged,
}: RulesTableProps) => {
    const theme = useTheme();
    const errorCtx = useError();
    const [addOpen, setAddOpen] = useState(false);
    const [editRule, setEditRule] = useState<PricebookRule | null>(null);
    const [docsOpen, setDocsOpen] = useState(false);
    const [docsContent, setDocsContent] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<PricebookRule | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [conflict, setConflict] = useState<ConflictPayload | null>(null);
    const conflictRetryRef = useState<{ fn: (() => Promise<void>) | null }>({ fn: null })[0];

    const enrichedRules = useMemo(() => {
        return [...rules]
            .map((rule) => ({ ...rule, info: lookup(catalog, rule.service_detail_id) }))
            .sort((a, b) => {
                if (a.info.repair_type !== b.info.repair_type) {
                    return a.info.repair_type.localeCompare(b.info.repair_type);
                }
                if (a.info.part !== b.info.part) {
                    return a.info.part.localeCompare(b.info.part);
                }
                if (a.info.position !== b.info.position) {
                    return a.info.position.localeCompare(b.info.position);
                }
                return (b.rank ?? 0) - (a.rank ?? 0);
            });
    }, [rules, catalog]);

    const handleLoadDocs = async (): Promise<void> => {
        try {
            const res = await pricebookApi.getDocumentation();
            if (res.data?.payload) {
                setDocsContent(res.data.payload);
                setDocsOpen(true);
            }
        } catch {
            errorCtx.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: 'Error loading documentation' },
            });
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pricebook || !deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await pricebookApi.deleteRule(pricebook.id, deleteTarget.id);
            errorCtx.DISPATCH({
                type: 'NO_ERROR',
                payload: { alert: 'Rule deleted successfully' },
            });
            setDeleteTarget(null);
            onRulesChanged();
        } catch {
            errorCtx.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: 'Error deleting rule' },
            });
        } finally {
            setDeleting(false);
        }
    };

    const titleText =
        (pricebook?.company_name ?? companyName)
            ? `${pricebook?.company_name ?? companyName} - ${pricebook?.name ?? 'Pricebook'}`
            : pricebook?.name ?? 'Pricebook';

    return (
        <Box sx={styles.card(theme)}>
            <Box sx={styles.header}>
                <Box sx={styles.titleBlock}>
                    <Typography sx={styles.title}>Pricebook Rules</Typography>
                    {pricebook && <Typography sx={styles.subtitle}>{titleText}</Typography>}
                </Box>
                <Box sx={styles.actions}>
                    <IconButton onClick={() => void handleLoadDocs()} title="Rule Ranking Documentation">
                        <HelpIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={styles.addButton(theme)}
                        onClick={() => {
                            setEditRule(null);
                            setAddOpen(true);
                        }}
                        disabled={!pricebook}
                    >
                        Add Rule
                    </Button>
                </Box>
            </Box>
            <TableContainer sx={styles.tableContainer}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Type</TableCell>
                            <TableCell>Repair Type</TableCell>
                            <TableCell>Part</TableCell>
                            <TableCell>Position</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Rank</TableCell>
                            <TableCell>Year Range</TableCell>
                            <TableCell>Make</TableCell>
                            <TableCell>Model</TableCell>
                            <TableCell>Special Order</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={11} sx={styles.placeholder}>
                                    Loading rules...
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && enrichedRules.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} sx={styles.placeholder}>
                                    No rules found. Click &lsquo;Add Rule&rsquo; to create one.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            enrichedRules.map((rule, idx) => {
                                const prev = idx > 0 ? enrichedRules[idx - 1] : null;
                                const isNewGroup =
                                    !prev ||
                                    prev.info.repair_type !== rule.info.repair_type ||
                                    prev.info.part !== rule.info.part ||
                                    prev.info.position !== rule.info.position;
                                return (
                                    <Fragment key={rule.id}>
                                        {isNewGroup && idx > 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={11}
                                                    sx={{ height: 8, p: 0, ...styles.groupSep(theme) }}
                                                />
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell>{rule.info.service_type}</TableCell>
                                            <TableCell sx={{ fontWeight: isNewGroup ? 600 : 400 }}>
                                                {rule.info.repair_type}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: isNewGroup ? 600 : 400 }}>
                                                {rule.info.part}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: isNewGroup ? 600 : 400 }}>
                                                {rule.info.position}
                                            </TableCell>
                                            <TableCell>
                                                {rule.price != null ? (
                                                    `$${rule.price.toFixed(2)}`
                                                ) : (
                                                    <Typography component="span" sx={styles.quoteRequired}>
                                                        Quote Required
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{rule.rank ?? '-'}</TableCell>
                                            <TableCell>{formatYearRange(rule.year_range)}</TableCell>
                                            <TableCell>{formatList(rule.make, 'All Makes')}</TableCell>
                                            <TableCell>{formatList(rule.model, 'All Models')}</TableCell>
                                            <TableCell>
                                                {rule.special_order && (
                                                    <Box component="span" sx={styles.chip(theme)}>
                                                        Special Order
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    title="Edit rule"
                                                    onClick={() => {
                                                        setEditRule(rule);
                                                        setAddOpen(true);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="Delete rule"
                                                    onClick={() => setDeleteTarget(rule)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <AddRuleDialog
                open={addOpen}
                pricebook={pricebook}
                catalog={catalog}
                vehicleMakes={vehicleMakes}
                editRule={editRule}
                onClose={() => {
                    setAddOpen(false);
                    setEditRule(null);
                }}
                onAdded={onRulesChanged}
                onConflict={(data, retry) => {
                    conflictRetryRef.fn = retry;
                    setConflict(data);
                }}
            />
            <ConflictDialog
                open={Boolean(conflict)}
                conflictData={conflict}
                catalog={catalog}
                onCancel={() => {
                    setConflict(null);
                    conflictRetryRef.fn = null;
                }}
                onForceUpdate={() => {
                    const retry = conflictRetryRef.fn;
                    conflictRetryRef.fn = null;
                    setConflict(null);
                    if (retry) void retry();
                }}
            />
            <DocumentationModal
                open={docsOpen}
                content={docsContent}
                onClose={() => setDocsOpen(false)}
            />
            <ConfirmDialog
                open={Boolean(deleteTarget)}
                title="Delete rule?"
                message="Are you sure you want to delete this rule?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                danger
                loading={deleting}
                onConfirm={() => void confirmDelete()}
                onCancel={() => setDeleteTarget(null)}
            />
        </Box>
    );
};

export default RulesTable;
